import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
    Alert,
    Box,
    Button,
    Divider,
    Grid,
    Typography,
} from '@mui/material';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { Title, useNotify } from 'react-admin';
import { ExpansionPanel, InputField } from '@/components/common';
import {
    IrdRateConfigBO,
    IrdRateNodes,
    IrdRateService,
} from '@/services/manageService/irdRateService';

type RateField = { itemKey: string; label: string };

type NodeSection = {
    nodeCode: string;
    title: string;
    fields: RateField[];
};

const SECTIONS: NodeSection[] = [
    {
        nodeCode: 'BS-NCA-PPE',
        title: 'Property, plant and equipment (BS-NCA-PPE)',
        fields: [
            { itemKey: 'locationOfPropertyRow', label: 'Leasehold land and building / Location of property' },
            { itemKey: 'commercialAllowanceRow', label: 'Leasehold improvement (Commercial building allowance)' },
            { itemKey: 'industrialAllowanceRow', label: 'Leasehold improvement (Industrial building allowance)' },
            { itemKey: 'furnitureFixtureRow', label: 'Furniture and Fixture' },
            { itemKey: 'computerHardSoftRow', label: 'Computer hardware and software' },
            { itemKey: 'fullPayMotorRow', label: '(Full pay) Motor Vehicle' },
            { itemKey: 'hirePurchaseMotorRow', label: '(Hire purchase) Motor Vehicle' },
            { itemKey: 'machineryRow', label: 'Machineries' },
            { itemKey: 'liftEscalatorRow', label: 'Lifts and escalators (electric)' },
            { itemKey: 'equipmentRow', label: 'Equipments (default; per-annual override still applies)' },
        ],
    },
    {
        nodeCode: 'BS-NCA-IA',
        title: 'Intangible assets (BS-NCA-IA)',
        fields: [{ itemKey: 'rate', label: 'Node IRD rate' }],
    },
    {
        nodeCode: 'BS-NCA-ITL',
        title: 'Investment in taxi cabs and licenses (BS-NCA-ITL)',
        fields: [
            { itemKey: 'taxiLicenseRow', label: 'Taxi Licensee' },
            { itemKey: 'fullPayTaxiCabRow', label: '(Full Pay) Taxi Cabs' },
            { itemKey: 'hirePurchaseTaxiCabRow', label: '(Hire Purchase) Taxi Cabs' },
        ],
    },
    {
        nodeCode: 'BS-NCA-ISUB',
        title: 'Investment in a subsidiary (BS-NCA-ISUB)',
        fields: [{ itemKey: 'rate', label: 'Node IRD rate' }],
    },
    {
        nodeCode: 'BS-NCA-IASS',
        title: 'Investment in an associate (BS-NCA-IASS)',
        fields: [{ itemKey: 'rate', label: 'Node IRD rate' }],
    },
    {
        nodeCode: 'BS-CA-IOT',
        title: 'Investment on trusts (BS-CA-IOT)',
        fields: [{ itemKey: 'rate', label: 'Node IRD rate' }],
    },
];

const BUILTIN_DEFAULTS: IrdRateNodes = {
    'BS-NCA-PPE': {
        locationOfPropertyRow: 10,
        commercialAllowanceRow: 4,
        industrialAllowanceRow: 4,
        furnitureFixtureRow: 20,
        computerHardSoftRow: 100,
        fullPayMotorRow: 30,
        hirePurchaseMotorRow: 30,
        machineryRow: 30,
        liftEscalatorRow: 10,
        equipmentRow: 20,
    },
    'BS-NCA-IA': { rate: 0 },
    'BS-NCA-ITL': {
        taxiLicenseRow: 0,
        fullPayTaxiCabRow: 30,
        hirePurchaseTaxiCabRow: 30,
    },
    'BS-NCA-ISUB': { rate: 0 },
    'BS-NCA-IASS': { rate: 0 },
    'BS-CA-IOT': { rate: 0 },
};

function mergeNodes(raw?: IrdRateNodes | null): IrdRateNodes {
    const next: IrdRateNodes = {};
    for (const section of SECTIONS) {
        const fromApi = raw?.[section.nodeCode] ?? {};
        const fromDefault = BUILTIN_DEFAULTS[section.nodeCode] ?? {};
        const items: Record<string, number> = {};
        for (const field of section.fields) {
            const v = fromApi[field.itemKey] ?? fromDefault[field.itemKey] ?? 0;
            items[field.itemKey] = Number(v);
        }
        next[section.nodeCode] = items;
    }
    return next;
}

function PanelTitle({ icon, label }: { icon: ReactNode; label: string }) {
    return (
        <Box display="flex" alignItems="center" gap={1} width="100%">
            {icon}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                {label}
            </Typography>
        </Box>
    );
}

export default function IrdRateConfig() {
    const notify = useNotify();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [nodes, setNodes] = useState<IrdRateNodes>(mergeNodes());

    const loadConfig = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await IrdRateService.getConfig();
            const data = (res as { data?: { nodes?: IrdRateNodes } })?.data ?? res;
            setNodes(mergeNodes((data as { nodes?: IrdRateNodes })?.nodes));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load IRD rate configuration';
            setError(msg);
            notify(msg, { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const setRate = (nodeCode: string, itemKey: string, raw: string) => {
        const n = Number(raw);
        const value = Number.isFinite(n) ? n : 0;
        setNodes(prev => ({
            ...prev,
            [nodeCode]: { ...prev[nodeCode], [itemKey]: value },
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const bo: IrdRateConfigBO = { nodes };
            await IrdRateService.updateConfig(bo);
            notify('IRD rate configuration saved', { type: 'success' });
            await loadConfig();
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save IRD rate configuration';
            notify(msg, { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box p={2}>
                <Title title="IRD Rate" />
                <Typography color="text.secondary">Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box p={2}>
            <Title title="IRD Rate" />
            {error ? (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={loadConfig}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            ) : null}
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CalculateOutlinedIcon color="primary" />
                <Typography variant="h5">IRD Rate Defaults</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Global IRD rate initialization for FS nodes. PPE working paper reads these defaults on GET;
                Equipments may still override per annual via equipmentIrdRate.
            </Typography>

            <Box sx={{ border: 1, borderColor: 'divider', mb: 3 }}>
                {SECTIONS.map((section, idx) => (
                    <ExpansionPanel
                        key={section.nodeCode}
                        title={
                            <PanelTitle
                                icon={<CalculateOutlinedIcon fontSize="small" color="primary" />}
                                label={section.title}
                            />
                        }
                        defaultExpanded={idx === 0}
                        titleVariant="subtitle2"
                    >
                        <Grid container spacing={2}>
                            {section.fields.map(field => (
                                <Grid key={field.itemKey} size={{ xs: 12, md: 6 }}>
                                    <InputField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label={`${field.label} (%)`}
                                        value={String(nodes[section.nodeCode]?.[field.itemKey] ?? 0)}
                                        onChange={e => setRate(section.nodeCode, field.itemKey, e.target.value)}
                                        inputProps={{ min: 0, max: 100, step: 1 }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </ExpansionPanel>
                ))}
            </Box>

            <Divider sx={{ mb: 2 }} />
            <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
            >
                {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
        </Box>
    );
}
